
import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AuthSession' });

export const useAuthSession = () => {
  const pendingAuthActionRef = useRef<boolean>(false);

  const refreshSession = useCallback(async (): Promise<Session | null> => {
    log.info('Refreshing session via getSession', new Date().toISOString());
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        log.error('Session refresh error:', error);
        throw error;
      }
      
      log.info('Session refresh result:', {
        hasSession: !!data.session,
        expiresAt: data.session?.expires_at,
        timestamp: new Date().toISOString()
      });
      
      // If we have a session, validate it works with auth.uid()
      if (data.session) {
        try {
          const { error: authError } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', data.session.user.id)
            .maybeSingle();

          if (authError) {
            log.error('Session invalid - auth.uid() not working, cleaning up');
            await supabase.auth.signOut({ scope: 'local' });
            return null;
          }
        } catch (validationError) {
          log.error('Session validation failed:', validationError);
          await supabase.auth.signOut({ scope: 'local' });
          return null;
        }
      }
      
      return data.session;
    } catch (error) {
      log.error('Error in refreshSession:', error);
      throw error;
    }
  }, []);

  const login = useCallback(async () => {
    log.info('Login called', new Date().toISOString());
    if (pendingAuthActionRef.current) {
      log.info('Login already in progress, ignoring duplicate call');
      return null;
    }
    
    pendingAuthActionRef.current = true;
    
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        log.error('Login error:', error);
        throw error;
      }
      
      if (data?.session) {
        log.info(`Login - Found existing session:`, {
          userId: data.session.user.id,
        });
      } else {
        log.info('No active session found, user needs to authenticate');
      }
      
      return data?.session || null;
    } catch (error) {
      log.error('Error during login check:', error);
      throw error;
    } finally {
      pendingAuthActionRef.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    log.info('Logout called', new Date().toISOString());
    if (pendingAuthActionRef.current) {
      log.info('Logout already in progress, ignoring duplicate call');
      return;
    }
    
    pendingAuthActionRef.current = true;
    
    try {
      const { error } = await supabase.auth.signOut({
        scope: 'local' // Only sign out locally, don't invalidate all sessions
      });
      
      if (error) {
        log.error('Logout error:', error);
        throw error;
      }
      
      // Clear any cached data after logout
      localStorage.removeItem('sb-last-refresh');
      
      log.info('Logout completed successfully');
    } catch (error) {
      log.error('Logout error:', error);
      throw error;
    } finally {
      pendingAuthActionRef.current = false;
    }
  }, []);

  return {
    refreshSession,
    login,
    logout,
    pendingAuthActionRef
  };
};
